import { z } from "zod";
import { MIX_WITH_RELATIONS, MIX_WITHOUT_RELATIONS } from "../../../domain/exercises/validate.ts";
import { relationProposalSchema, wordTypeSchema } from "../../../contracts/ai/schemas.ts";

const drillText = z.string().min(1).max(400);
const answerText = z.string().min(1).max(80);
const simpleEnglish = "The simplest everyday English a learner can understand. Short and normal.";

export const simpleDrillSchema = z.object({
  question: drillText.describe(`${simpleEnglish} A full question, never a number.`),
  correctAnswer: answerText,
  acceptedAnswers: z.array(answerText).max(6).optional().default([]),
  explanation: z.string().min(1).max(280).describe(`${simpleEnglish} One short reason.`),
});

export const confusableDrillSchema = simpleDrillSchema.extend({
  options: z
    .array(answerText)
    .min(2)
    .max(4)
    .describe("Different word labels for the choices. Not IDs."),
});

export const packageCardSchema = z.object({
  conflict: z.boolean().optional().default(false),
  conflictMessage: z
    .string()
    .max(400)
    .nullable()
    .optional()
    .default(null)
    .describe(`${simpleEnglish} Why the locked meaning cannot be supported.`),
  type: wordTypeSchema,
  shortLabel: z.string().min(1).max(120).describe(simpleEnglish),
  definition: z.string().min(1).max(400).describe(simpleEnglish),
  russianHint: z.string().max(200).nullable(),
  example: z.string().min(1).max(280).describe(simpleEnglish),
  memoryKey: z.string().max(160).nullable().describe(`${simpleEnglish} Or null when it does not help.`),
  collocations: z.array(z.string().min(1).max(80).describe(simpleEnglish)).max(3),
  relations: z
    .array(
      relationProposalSchema.extend({
        explanation: z.string().min(1).max(400).describe(`${simpleEnglish} How this meaning differs.`),
      }),
    )
    .max(6),
  reviewNote: z.string().max(400).nullable().describe(`${simpleEnglish} Or null when nothing important changed.`),
});

export const groupedExercisesLooseSchema = z.object({
  contextRecall: z.array(simpleDrillSchema).min(1).max(20),
  meaningToWord: z.array(simpleDrillSchema).min(1).max(8),
  confusableContext: z.array(confusableDrillSchema).max(4).optional().default([]),
});

export function groupedExercisesRequestSchema(hasUsefulRelations: boolean) {
  const mix = hasUsefulRelations ? MIX_WITH_RELATIONS : MIX_WITHOUT_RELATIONS;
  const contextRecall = z
    .array(simpleDrillSchema)
    .length(mix.context_recall)
    .describe(
      "Fill-in-the-blank questions. Each item is an object with question, correctAnswer, acceptedAnswers, and explanation.",
    );
  const meaningToWord = z
    .array(simpleDrillSchema)
    .length(mix.meaning_to_word)
    .describe("Questions that ask for the word from its meaning. Each item is an object, never a number.");
  if (hasUsefulRelations) {
    return z.object({
      contextRecall,
      meaningToWord,
      confusableContext: z
        .array(confusableDrillSchema)
        .length(mix.confusable_context)
        .describe(
          "Multiple-choice questions. Each item is an object with question, answers, and word labels.",
        ),
    });
  }
  return z.object({
    contextRecall,
    meaningToWord,
    confusableContext: z.array(confusableDrillSchema).max(0).optional().default([]),
  });
}

export type PackageCard = z.infer<typeof packageCardSchema>;
export type SimpleDrill = z.infer<typeof simpleDrillSchema>;
export type ConfusableDrill = z.infer<typeof confusableDrillSchema>;
export type GroupedExercises = z.infer<typeof groupedExercisesLooseSchema>;
